import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Chip,
  Stack
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import TeamImage from '../assets/Team_alamen.png';
import PageHeader from './pages/PageHeader';
import { SITE_URL } from '../lib/seo';

export default function About() {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();

  const focus = [
    t('about.focus.localizedFineTuning'),
    t('about.focus.sectorSpecificSolutions'),
    t('about.focus.adaptiveLearning')
  ];

  const applied = [
    {
      title: t('about.applied.lostFoundTitle'),
      body: t('about.applied.lostFoundBody')
    },
    {
      title: t('about.applied.legalTitle'),
      body: t('about.applied.legalBody')
    }
  ];

  const alignment = [
    t('about.alignment.uaeAi2031'),
    t('about.alignment.digitalDubai'),
    t('about.alignment.dff')
  ];

  const flagship = [
    {
      title: t('about.flagship.lostFoundTitle'),
      body: t('about.flagship.lostFoundBody')
    },
    {
      title: t('about.flagship.legalTitle'),
      body: t('about.flagship.legalBody')
    }
  ];

  return (
    <Box>
      <Helmet>
        <title>About | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/about`} />
      </Helmet>
      <PageHeader
        eyebrow={t('about.eyebrow')}
        title={t('about.title')}
        body={t('about.subtitle')}
      />
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {[t('about.badge.algorithms'), t('about.badge.appliedIntelligence'), t('about.badge.dubaiFocus')].map(
            (b) => (
              <Chip key={b} label={b} variant="outlined" />
            )
          )}
        </Stack>
      </Container>

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                {t('about.algorithmsTitle')}
              </Typography>
              <Typography sx={{ opacity: 0.9, mb: 2 }}>{t('about.algorithmsBody')}</Typography>
            </Box>

            <Typography variant="h6" fontWeight={800} sx={{ mt: 3, mb: 1 }}>
              {t('about.focusTitle')}
            </Typography>

            <Stack spacing={1.2}>
              {focus.map((item) => (
                <Paper
                  key={item}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{item}</Typography>
                </Paper>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper
              component={motion.div}
              variant="outlined"
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
              }}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Box
                component="img"
                src={TeamImage}
                alt={t('about.teamImageAlt')}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  display: 'block',
                  aspectRatio: { xs: '16 / 10', md: '16 / 9' },
                  objectFit: 'cover',
                  maxHeight: { xs: 260, sm: 320, md: 420 }
                }}
              />
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                  {t('about.teamTitle')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {t('about.teamBody')}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1, mt: 2 }}>
              {t('about.appliedTitle')}
            </Typography>
            <Grid container spacing={2}>
              {applied.map((a) => (
                <Grid key={a.title} item xs={12} md={6}>
                  <Paper
                    component={motion.div}
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 4,
                      height: '100%',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
                      }
                    }}
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                  >
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                      {a.title}
                    </Typography>
                    <Typography sx={{ opacity: 0.9 }}>{a.body}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
              {t('about.alignmentTitle')}
            </Typography>
            <Stack spacing={1.2}>
              {alignment.map((item) => (
                <Paper
                  key={item}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{item}</Typography>
                </Paper>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
              {t('about.flagshipTitle')}
            </Typography>
            <Stack spacing={1.6}>
              {flagship.map((f) => (
                <Paper
                  key={f.title}
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Typography fontWeight={800} sx={{ mb: 0.5 }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ opacity: 0.9 }}>{f.body}</Typography>
                </Paper>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Paper
              component={motion.div}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                bgcolor: 'rgba(102,126,234,0.08)'
              }}
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
            >
              <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>
                {t('about.poweringTitle')}
              </Typography>
              <Typography sx={{ opacity: 0.95, maxWidth: 1000 }}>
                {t('about.poweringBody')}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" fontWeight={900} sx={{ mb: 1 }}>
              {t('about.companyTitle')}
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>{t('about.companyBody')}</Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
